import { forwardRef, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import { addDays, addYears } from 'date-fns';
import {
  CustodianTypes,
  IraUserFieldNames,
  IraUserRequestForm,
  ProfileListResponse,
  UserManagerInfo,
  DefaultUserManagersResponse,
  UserProfileTypes,
} from 'services/requests';
import {
  convertDate,
  etzFeeTransform,
  lowerCaseTransform,
  custodyFeePercentageTransform,
  custodyFeeAbsoluteTransform,
  formatInputDate,
  parseInputDate,
  checkDefaultUserManager,
  cellNumberTransform,
} from 'utils';
import { USER_STATUS } from 'constants/status';
import { mapIndependentIraTypeValueToKey } from 'screens/IndependentIra/IraIndependentIra.utils';
import Paper from 'components/Paper';
import Field from 'components/Field';
import { AlertLockUser, AlertsList } from 'components/Alerts';
import ProfileList from 'components/ProfileList';
import DateField from 'components/DateField';
import UserManager from 'components/UserManager';
import Picker from 'components/Picker';
import {
  disabledField,
  disabledEmail,
  disabledNextBillingDate,
  disabledCustodian,
} from './IraUserForm.statuses';

import styles from './IraUserForm.module.css';
import messages from './IraUserForm.messages';

interface Props {
  form: UseFormReturn<IraUserRequestForm>;
  profileType?: string;
  profileStatus?: string;
  lastActivity?: string;
  accountNumber?: string;
  userName?: string;
  phoneNumber?: string;
  created?: string;
  isNewUser?: boolean;
  isLocked?: boolean;
  hoursToAutoUnlock?: number;
  lockReason?: string;
  hasOtherAccount?: boolean;
  userDataIsInconsistent?: boolean;
  otherAccounts?: ProfileListResponse;
  email?: string;
  errors?: string[];
  approvalDateTime?: string;
  nextBillingDate?: string;
  defaultEtzFee?: number;
  defaultAnnualFee?: number;
  defaultAnnualFeeFreelancer?: number;
  defaultCustodyFeePercentage?: number;
  defaultCustodyFeePercentageIraClub?: number;
  custodianType?: string;
  shouldEditCustodian?: boolean;
  userManagers?: UserManagerInfo[];
  employerId?: number;
  defaultUserManager?: DefaultUserManagersResponse;
  employerName?: string;
}

const IraUserForm = forwardRef<HTMLFormElement, Props>(
  (
    {
      form,
      profileType,
      profileStatus,
      lastActivity,
      accountNumber,
      userName,
      phoneNumber,
      created,
      isNewUser,
      isLocked,
      hoursToAutoUnlock,
      lockReason,
      hasOtherAccount,
      userDataIsInconsistent,
      otherAccounts,
      email,
      errors,
      approvalDateTime,
      nextBillingDate,
      defaultEtzFee,
      defaultCustodyFeePercentageIraClub,
      custodianType,
      shouldEditCustodian,
      userManagers,
      defaultUserManager,
      employerName,
    },
    ref,
  ) => {
    const [custodianSelected, setCustodianSelected] = useState<
      string | number | undefined
    >(custodianType);
    const [profileTypeSelected, setProfileTypeSelected] = useState<
      string | number | undefined
    >(profileType);
    const [annualFeeFocused, setAnnualFeeFocused] = useState(false);
    const [custodyFeePercentageFocused, setCustodyFeePercentageFocused] = useState(false);
    const [custodyFeeAbsoluteFocused, setCustodyFeeAbsoluteFocused] = useState(false);

    useEffect(() => {
      setCustodianSelected(custodianType);
    }, [custodianType]);

    useEffect(() => {
      setProfileTypeSelected(profileType);
    }, [profileType]);

    const fieldLength = {
      firstName: 150,
      middleName: 150,
      lastName: 150,
    };

    const RenderSubtitle = (): JSX.Element => {
      if (isNewUser)
        return <p className={styles.iraUserForm__subtitle}>{messages.subtitle}</p>;

      return (
        <div className={styles.iraUserForm__subtitle}>
          <p>
            {!!created && <span>{`${messages.created}${convertDate(created)}`}</span>}
            {!!accountNumber && (
              <span>{`${messages.accountNumber} #${accountNumber}`}</span>
            )}
          </p>
          <p>
            {!!lastActivity ? (
              <span>{`${messages.activity} ${convertDate(lastActivity)}`}</span>
            ) : (
              <span>{messages.noActivity}</span>
            )}
            {!!userName && <span>{`${messages.userName} ${userName}`}</span>}
          </p>
          <p>
            {custodianType === CustodianTypes.solera ? (
              <span>
                {!!approvalDateTime
                  ? `${messages.approvalDateTime} ${convertDate(approvalDateTime)}`
                  : messages.noApprovalDateTime}
              </span>
            ) : (
              <span>
                {!!nextBillingDate
                  ? `${messages.nextBillingDate} ${convertDate(
                      parseInputDate(nextBillingDate).toISOString(),
                    )}`
                  : messages.noNextBillingDate}
              </span>
            )}
            {!!phoneNumber && (
              <span>{`${messages.phoneNumber} ${cellNumberTransform(phoneNumber)}`}</span>
            )}
          </p>
        </div>
      );
    };

    const isDisabled =
      disabledField(profileStatus) || hasOtherAccount || userDataIsInconsistent;
    const isDisabledEmail =
      (!!profileStatus && disabledEmail(profileStatus)) || isDisabled || isNewUser;
    const isDisabledNextBillingDate =
      (!!profileStatus && disabledNextBillingDate(profileStatus)) || isNewUser;
    const isDisabledCustodian =
      ((!!profileStatus && disabledCustodian(profileStatus)) || userDataIsInconsistent) &&
      !shouldEditCustodian;
    const isDisabledAnnualFee = custodianSelected !== CustodianTypes.solera || isDisabled;
    const definedDefaultUserManager =
      (isNewUser || custodianSelected !== custodianType) &&
      defaultUserManager &&
      checkDefaultUserManager(defaultUserManager, custodianSelected);
    const shouldShowUserManagerAlert =
      !!userManagers?.length &&
      !userManagers[0]?.custodianType &&
      custodianSelected !== custodianType;
    const linkedUserManagers =
      (shouldShowUserManagerAlert && userManagers) ||
      (custodianSelected === custodianType && userManagers);

    const { dirtyFields } = form.formState;

    const onChangeCustodyFee = () => {
      if (profileStatus === USER_STATUS.active && approvalDateTime) {
        const values = form.getValues();
        const nextBillingDate = values[IraUserFieldNames.nextBillingDate];
        const custodyFeeAbsolute = values[IraUserFieldNames.custodyFeeAbsolute];
        const custodyFeePercentage = values[IraUserFieldNames.custodyFeePercentage];

        if ((custodyFeeAbsolute || custodyFeePercentage) && !nextBillingDate) {
          const currentDatePlusDay = addDays(new Date(), 1);
          const approvalDatePlusYear = addYears(new Date(approvalDateTime), 1);
          const newNextBillingDate = formatInputDate(
            approvalDatePlusYear > currentDatePlusDay
              ? approvalDatePlusYear
              : currentDatePlusDay,
          ).replace(/[^\d]/g, '') as never;

          form.setValue(IraUserFieldNames.nextBillingDate, newNextBillingDate, {
            shouldDirty: true,
          });
        }
      }
    };

    const onChangeCustodianType = (newCustodian: string | number | undefined) => {
      setCustodianSelected(newCustodian);

      form.setValue(IraUserFieldNames.etzFee, defaultEtzFee as never, {
        shouldDirty: true,
      });
      form.setValue(
        IraUserFieldNames.custodyFeePercentage,
        defaultCustodyFeePercentageIraClub?.toString() as never,
        {
          shouldDirty: true,
        },
      );
    };

    const watchCustodianTypeField = form.watch(IraUserFieldNames.custodianType);

    useEffect(() => {
      if (watchCustodianTypeField === '') {
        setCustodianSelected(undefined);
      }
      if (
        Object.values(CustodianTypes).find(
          (custodianType) => custodianType === watchCustodianTypeField,
        )
      ) {
        setCustodianSelected(watchCustodianTypeField);
      }
    }),
      [watchCustodianTypeField];

    return (
      <>
        <DevTool control={form.control} />
        <Paper className={styles.iraUserForm}>
          <AlertLockUser
            hoursToAutoUnlock={hoursToAutoUnlock}
            lockReason={lockReason}
            isLocked={isLocked}
          />
          {!!errors?.length && <AlertsList errors={errors} />}

          <h1 className={styles.iraUserForm__title}>{messages.title}</h1>
          <RenderSubtitle />
          <form ref={ref}>
            <Field
              type={'text'}
              form={form}
              name={IraUserFieldNames.firstName}
              placeholder={messages.placeholder.firstName}
              className={styles.iraUserForm__input}
              labelClassname={styles.iraUserForm__inputLabel}
              disabled={isDisabled}
              maxLength={fieldLength.firstName}
            />

            <Field
              type={'text'}
              form={form}
              name={IraUserFieldNames.middleName}
              placeholder={messages.placeholder.middleName}
              className={styles.iraUserForm__input}
              labelClassname={styles.authUserForm__inputLabel}
              disabled={isDisabled}
              maxLength={fieldLength.middleName}
            />

            <Field
              type={'text'}
              form={form}
              name={IraUserFieldNames.lastName}
              placeholder={messages.placeholder.lastName}
              className={styles.iraUserForm__input}
              labelClassname={styles.iraUserForm__inputLabel}
              disabled={isDisabled}
              maxLength={fieldLength.lastName}
            />

            <Field
              type={'text'}
              form={form}
              name={IraUserFieldNames.email}
              placeholder={messages.placeholder.email}
              className={styles.iraUserForm__input}
              labelClassname={styles.iraUserForm__inputLabel}
              disabled={isDisabledEmail}
              {...lowerCaseTransform}
            />

            {employerName && (
              <Field
                type={'text'}
                form={form}
                name={IraUserFieldNames.employerName}
                placeholder={messages.placeholder.employerName}
                defaultValue={employerName}
                className={styles.iraUserForm__input}
                labelClassname={styles.iraUserForm__inputLabel}
                disabled={true}
              />
            )}

            <Picker
              form={form}
              name={IraUserFieldNames.custodianType}
              options={Object.values(CustodianTypes)}
              placeholder={messages.placeholder.custodianType}
              className={styles.iraUserForm__input}
              onSelect={onChangeCustodianType}
              selectedOption={custodianSelected}
              size={'large'}
              disabled={isDisabledCustodian}
              disabledOption={CustodianTypes.solera}
            />

            <Picker
              form={form}
              name={IraUserFieldNames.profileType}
              options={[UserProfileTypes.sepIra]}
              placeholder={messages.placeholder.profileType}
              className={styles.iraUserForm__input}
              onSelect={setProfileTypeSelected}
              selectedOption={profileTypeSelected}
              size={'large'}
              disabled={true}
            />

            <Field
              type={'text'}
              form={form}
              name={IraUserFieldNames.etzFee}
              placeholder={messages.placeholder.etzFee}
              className={styles.iraUserForm__input}
              labelClassname={styles.iraUserForm__inputLabel}
              {...etzFeeTransform}
            />

            <p className={styles.iraUserForm__fieldsGroupTitle}>
              {messages.custodyFeeTitle}
            </p>

            <DateField
              form={form}
              name={IraUserFieldNames.nextBillingDate}
              placeholder={
                !isDisabledNextBillingDate &&
                (dirtyFields.custodyFeePercentage || dirtyFields.custodyFeeAbsolute)
                  ? `${messages.placeholder.nextBillingDate}*`
                  : messages.placeholder.nextBillingDate
              }
              className={styles.iraUserForm__input}
              labelClassname={styles.authUserForm__inputLabel}
              from={addDays(new Date(), 1)}
              disabled={isDisabledNextBillingDate}
            />

            <Field
              type={'text'}
              form={form}
              name={IraUserFieldNames.custodyFeePercentage}
              placeholder={
                dirtyFields.nextBillingDate && !dirtyFields.custodyFeeAbsolute
                  ? `${messages.placeholder.custodyFeePercentage}*`
                  : messages.placeholder.custodyFeePercentage
              }
              className={styles.iraUserForm__input}
              labelClassname={styles.authUserForm__inputLabel}
              onFocus={() => setCustodyFeePercentageFocused(true)}
              onBlur={() => setCustodyFeePercentageFocused(false)}
              mask={(value) =>
                custodyFeePercentageTransform.mask(value, {
                  isFocused: custodyFeePercentageFocused,
                })
              }
              mapper={custodyFeePercentageTransform.mapper}
              onChangeText={onChangeCustodyFee}
            />

            <Field
              type={'text'}
              form={form}
              name={IraUserFieldNames.custodyFeeAbsolute}
              placeholder={
                dirtyFields.nextBillingDate && !dirtyFields.custodyFeePercentage
                  ? `${messages.placeholder.custodyFeeAbsolute}*`
                  : messages.placeholder.custodyFeeAbsolute
              }
              className={styles.iraUserForm__input}
              labelClassname={styles.authUserForm__inputLabel}
              onFocus={() => setCustodyFeeAbsoluteFocused(true)}
              onBlur={() => setCustodyFeeAbsoluteFocused(false)}
              mask={(value) =>
                custodyFeeAbsoluteTransform.mask(value, {
                  ignoreEmpty: custodyFeeAbsoluteFocused,
                })
              }
              mapper={custodyFeeAbsoluteTransform.mapper}
              onChangeText={onChangeCustodyFee}
            />
          </form>

          {(linkedUserManagers ||
            (definedDefaultUserManager && !shouldShowUserManagerAlert)) && (
            <UserManager
              userManagers={linkedUserManagers || undefined}
              defaultUserManager={
                shouldShowUserManagerAlert
                  ? undefined
                  : definedDefaultUserManager || undefined
              }
              showAlert={shouldShowUserManagerAlert}
            />
          )}

          <ProfileList
            otherAccounts={otherAccounts}
            email={email}
            currentType={mapIndependentIraTypeValueToKey(profileType)}
          />
        </Paper>
      </>
    );
  },
);

export default IraUserForm;
